import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { Connector } from '@/types/core/connector';
import type { Page } from '@/types/shared/page';
import { isNilUUID, type UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

/**
 * List connectors for a given tenant.
 * Accepts optional paging args via `opts`.
 */
export function useListConnectors(
  tenantId: UUID,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
    enabled?: boolean;
  }
): UseInfiniteQueryResult<InfiniteData<Page<Connector>, unknown>, Error> {
  return useInfiniteQuery<
    Page<Connector>,
    Error,
    InfiniteData<Page<Connector>, unknown>
  >({
    queryKey: connectorQueryKeys.list(tenantId, opts?.limit, opts?.filter),
    queryFn: (context: QueryFunctionContext) => {
      const pageParam = context.pageParam as string | undefined;
      return connectorService.listConnectors(tenantId, {
        limit: opts?.limit,
        next: pageParam,
        filter: opts?.filter,
      });
    },
    enabled: (opts?.enabled ?? true) && !isNilUUID(tenantId),
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
  });
}
